<template>
  <div class="au-vdp">
    <audit-container class="editor-audit-container">
      <template v-slot:topBar>
        <steps-controller></steps-controller>
      </template>
      <template v-slot:main>
        <current-step-title></current-step-title>
        <vdp-group v-show="currentStep === STEP_NAME.VDP_TABLE"></vdp-group>
        <approval-review
          v-if="currentStep === STEP_NAME.APPROVAL"
          @change-checkout-checkbox="handleCheckoutCheckboxChange"
          @checkout-finish="handleCheckoutFinish"
        ></approval-review>
      </template>
    </audit-container>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, computed } from 'vue';

import pinia from '../core/pinia';
import { useConfigStore, useLineItemStore, useStepsStore, useVdpTableStore } from '../stores';

import AuditContainer from '../components/ui/AuditContainer.vue';
import StepsController from '../components/ui/StepsController.vue';
import CurrentStepTitle from '../components/ui/CurrentStepTitle.vue';
import ApprovalReview from '../components/approval-review/ApprovalReview.vue';
import VdpGroup from '../components/vdp-table/VdpGroup.vue';

import { E_DATASHEET_VIEW_EVENT } from '../types/components-events.enums';
import { E_SCENARIO, type ILineItem } from '../types/line-item.types';
import type { TCheckboxGroupSelectedItems } from '../types/checkbox-group.types';
import { E_STEP_NAME } from '../types/components.enums';
import { InnerVdpDataFormatter } from '../utils/inner-vdp-data-formatter';
import loadDatasets from '../utils/load-datasets';
import { InnerDatasetFormatter } from '../utils/inner-dataset-data-formatter';


export default defineComponent({
  name: 'AuVdp',
  components: {
    'audit-container': AuditContainer,
    'steps-controller': StepsController,
    'current-step-title': CurrentStepTitle,
    'approval-review': ApprovalReview,
    'vdp-group': VdpGroup,
  },
  emits: {
    [E_DATASHEET_VIEW_EVENT.CHANGE_CHECKOUT_CHECKBOX]: (checkboxGroupSelectedItems: TCheckboxGroupSelectedItems) => checkboxGroupSelectedItems,
    [E_DATASHEET_VIEW_EVENT.CHECKOUT_FINISH]: (lineItem: ILineItem | null) => lineItem,
  },
  setup(_, { emit, expose }) {
    const configStore = useConfigStore(pinia);
    const stepsStore = useStepsStore(pinia);
    const lineItemStore = useLineItemStore(pinia);
    const vdpTableStore = useVdpTableStore(pinia);

    const lineItemUpdateData = computed(() => configStore.getInitDataForLineItem);
    const currentStep = computed(() => (stepsStore.getCurrentStep) ? stepsStore.getCurrentStep.toLowerCase() : null);

    const STEP_NAME = E_STEP_NAME;

    function handleCheckoutCheckboxChange(checkboxGroupSelectedItems: TCheckboxGroupSelectedItems) {
      emit(E_DATASHEET_VIEW_EVENT.CHANGE_CHECKOUT_CHECKBOX, checkboxGroupSelectedItems);
      return checkboxGroupSelectedItems;
    }

    function handleCheckoutFinish(lineItem: ILineItem | null) {
      emit(E_DATASHEET_VIEW_EVENT.CHECKOUT_FINISH, lineItem);
      return lineItem;
    }

    async function getVdpData() {
      return await vdpTableStore.fetchVdpData();
    }

    onMounted(async () => {
      const lineItemUpdateDataProxy = Object.assign({}, lineItemUpdateData.value);
      lineItemUpdateDataProxy.scenario = E_SCENARIO.VDP;
      lineItemStore.updateLineItem(lineItemUpdateDataProxy);
      const designAtomsDataset = await getVdpData();
      if (designAtomsDataset) {
        const innerDataset = new InnerDatasetFormatter();
        innerDataset.fromDesignAtomsDataset(designAtomsDataset);
        const preparedDataset = InnerVdpDataFormatter({
          vdpDataWithSurface: innerDataset.datasets,
          useOriginalValue: true,
        });
        loadDatasets(preparedDataset.datasets, preparedDataset.surfacesData);
      }
    });

    expose({
      loadDatasets,
      InnerVdpDataFormatter,
    });

    return {
      STEP_NAME,
      handleCheckoutCheckboxChange,
      handleCheckoutFinish,
      currentStep,
    };
  },
});
</script>

<style lang="scss">
.error-message {
  color: red;
  font-size: 12px;
  margin-top: 5px;
}
</style>